import { CityCfg, FeatureCfg, OverrideCfg, PageMeta } from './seo-config.types';
import {
  AVERAGE_TRAVEL_TIME_TO_WORK,
  CRIME_RATE,
  GENDER_PAY_GAP,
  HEART_DISEASES_PREVALENCE,
  HOME_OWNERSHIP_RATIO,
  LIFE_EXPECTANCY,
  MEDIAN_AGE,
  MEDIAN_INCOME,
  PEOPLE_PER_MI2,
  POPULATION,
  POPULATION_GROWTH_TREND,
  SMOKING_RATE,
  WISE_INDEX_BY_COUNTRY,
} from '../shared/types/feature-data-type';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  STATE_LEVEL_LAYER,
} from '../map/mapbox/services/layer-store.service';

//TODO run export:seo script on each update here to generate static json,
// which can be used for sitemaps generation

export const FEATURES: Record<string, FeatureCfg> = {
  'crime-rate': {
    path: 'crime-rate',
    featureConst: CRIME_RATE,
    name: 'Crime Rate',
    description:
      'A snapshot of how safe or risky an area might be. It shows you the number of all types of crimes happening in that area for every 100,000 residents.',
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  population: {
    path: 'population',
    featureConst: POPULATION,
    name: 'Population',
    description:
      'Population data provides the number of people living in a specific area. Source: U.S. Census Bureau',
  },

  'gender-pay-gap': {
    path: 'gender-pay-gap',
    featureConst: GENDER_PAY_GAP,
    name: 'Gender pay gap',
    description:
      'A look at the income gap showing the percentage difference between the median earnings of men and women working full-time. Source: Map AI methodology',
  },

  'life-expectancy': {
    path: 'life-expectancy',
    featureConst: LIFE_EXPECTANCY,
    name: 'Life expectancy',
    description:
      'Life expectancy is a statistical measure that estimates the average number of years a person can expect to live. Source: National Center for Health Statistics (NCHS)',
  },

  'commute-time': {
    path: 'commute-time',
    featureConst: AVERAGE_TRAVEL_TIME_TO_WORK,
    name: 'Commute time',
    description:
      'See how long it typically takes to reach work in the area by exploring average travel times to work. Source: Map AI methodology',
    keywords: [
      'commute time',
      'average commute time',
      'commute times by city',
      'commute times by state',
      'commute times by neighborhood',
      'travel time to work'
    ]
  },
  'crime-map': {
    path: 'crime-map',
    featureConst: CRIME_RATE,
    name: 'Crime map USA | Map AI',
    description:
      'Get clear U.S. crime rate map by category and county. Total crime, community, business, violent, property.',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'criminal-map-usa': {
    path: 'criminal-map-usa',
    featureConst: CRIME_RATE,
    name: 'Map AI: Criminal map USA',
    description:
      'Get clear U.S. crime rate map by category and county. Total crime, community, business, violent, property.',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'crime-mapping-usa': {
    path: 'crime-mapping-usa',
    featureConst: CRIME_RATE,
    name: 'Crime map USA | Map AI',
    description:
      'Explore crime data and statistics with an interactive map: community crime rate, business crime rate,  violent crime rate, property crime rate. ',
    keywords: [
      'crime map',
      'crime mapping',
      'criminal map usa',
      'crime map usa',
      'crime maps',
      'crime rate map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'median-income-map': {
    path: 'median-income-map',
    featureConst: MEDIAN_INCOME,
    name: 'Map of median household income - by neighborhood',
    description:
      'Explore U.S. median household income—from census tracts to 0.5-mile areas. Includes demographics, housing, and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/5+median+income+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/5+median+income+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/5+median+income+map.jpg',
    },
  },

  'median-household-income-neighborhood': {
    path: 'median-household-income-neighborhood',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income map by neighborhood',
    description:
      'Explore U.S. median household income—from census tracts to 0.5-mile areas. Includes demographics, housing, and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'median-household-income-by-census-tract': {
    path: 'median-household-income-by-census-tract',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income by census tract map',
    description:
      'Explore U.S. median household income—from census tracts to 0.5-mile areas. Includes demographics, housing, and real estate data.',
    keywords: [
      'median household income map',
      'median income map',
      'map of median household income',
      'map of median income',
      'median income by zip code map',
      'median household income by zip code map',
      'area median income map',
      'census median income map',
      'hud median income map',
      'median household income by census tract map',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'education-rankings-by-state': {
    path: 'education-rankings-by-state',
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Education rankings by state & neighborhood',
    description:
      'Explore rankings for education by state and down to neighborhood. See high school rates, public school quality, and education completion levels.',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/8+states+ranked+by+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/8+states+ranked+by+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/8+states+ranked+by+education.jpg',
    },
  },

  'education-rankings-by-state-map-ai': {
    path: 'education-rankings-by-state-map-ai',
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Rankings for education by state | Map AI',
    description:
      'Explore rankings for education by state and down to neighborhood. See high school rates, public school quality, and education completion levels.',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/8+states+ranked+by+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/8+states+ranked+by+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/8+states+ranked+by+education.jpg',
    },
  },

  'median-income-by-state': {
    path: 'median-income-by-state',
    featureConst: MEDIAN_INCOME,
    name: 'Median income by state ranking — down to 0.5 mi neighborhood',
    description:
      '2024 median income by state ranking, yearly trends, and local data down to 0.5-mi areas across the U.S.',
    keywords: [
      'median income by state',
      'median average income by state',
      'median household income by state',
      'median family income by state',
      'states by median income',
      'state by state median income',
      'united states median income by year',
      'family median income by state',
      'highest median household income by state',
      'household median income by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/10+Median+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/10+Median+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/10+Median+income+by+state.jpg',
    },
  },

  'homeownership-rate-usa': {
    path: 'homeownership-rate-usa',
    featureConst: HOME_OWNERSHIP_RATIO,
    name: 'Map AI: Rate of homeownership in the USA, 2024',
    description:
      'Explore homeownership rates in the USA with our interactive map—down to 0.5-mile areas. Access data on demographics, income, safety & more.',
    keywords: ['rate of homeownership in US', 'us rates of homeownership'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/11+us+rates+of+homeownership.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/11+us+rates+of+homeownership.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/11+us+rates+of+homeownership.jpg',
    },
  },

  'median-age-by-state': {
    path: 'median-age-by-state',
    featureConst: MEDIAN_AGE,
    name: 'Map AI: Median age by state',
    description:
      'Explore the median age by state and down to 0.5 mi. See census-based age, median income & real estate insights—interactive and easy to compare.',
    keywords: [
      'median age by state',
      'median age for usa every state',
      'median age by state 2024',
      'median age by state census',
      'median age by us state',
      'median income by age and state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/12+median+age+for+usa+every+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/12+median+age+for+usa+every+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/12+median+age+for+usa+every+state.jpg',
    },
  },

  'smoking-rates-by-city': {
    path: 'smoking-rates-by-city',
    featureConst: SMOKING_RATE,
    name: 'Map AI: Smoking rates by cities',
    description:
      'Explore smoking rates by U.S. state—plus health, income & demographic data. Explore patterns with interactive maps down to 0.5-mile areas.',
    keywords: ['smoking rates by city'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/13+boston+smoking+rates.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/13+boston+smoking+rates.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/13+boston+smoking+rates.jpg',
    },
  },

  'population-density-by-state': {
    path: 'population-density-by-state',
    featureConst: PEOPLE_PER_MI2,
    name: 'Population density map by state - down to 0.5 mi neighborhood',
    description:
      'Explore our interactive population density map by state—zoom down to the 0.5-mile neighborhood level',
    keywords: [
      'population density map by state',
      'states by population density',
      'population density by state',
      'us states by population density',
      'population density by us state',
      'u.s. states by population density',
      'united states population density by state',
      'us population density by state',
      'states ranked by population density',
      'us states ranked by population density',
      'lowest population density by state',
      'list of us states by population density',
      'us state by population density',
      'largest states by population density',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/14+population+density+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/14+population+density+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/14+population+density+by+state.jpg',
    },
  },

  'states-by-population-density': {
    path: 'states-by-population-density',
    featureConst: PEOPLE_PER_MI2,
    name: 'States by population density — Map AI',
    description:
      'Explore our interactive population density map by state—zoom down to the 0.5-mile neighborhood level',
    keywords: [
      'population density map by state',
      'states by population density',
      'population density by state',
      'us states by population density',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/14+population+density+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/14+population+density+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/14+population+density+by+state.jpg',
    },
  },

  'us-states-by-median-income': {
    path: 'us-states-by-median-income',
    featureConst: MEDIAN_INCOME,
    name: 'Map AI: US states by median income',
    description:
      'US states by median income—down to 0.5-mile areas. Explore income growth, gender gaps & distribution.',
    keywords: [
      'us states by median income',
      'u.s. states by median income',
      'lowest median income by state',
      'median income by state',
      'median average income by state',
      'median household income by state',
      'median family income by state',
      'states by median income',
      'state by state median income',
      'united states median income by year',
      'family median income by state',
      'highest median household income by state',
      'household median income by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/16+median+average+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/16+median+average+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/16+median+average+income+by+state.jpg',
    },
  },

  'median-household-income-for-each-state': {
    path: 'median-household-income-for-each-state',
    featureConst: MEDIAN_INCOME,
    name: 'Median income by state ranking — Map AI',
    description:
      'Median income by state ranking data, as of 2024: #1 Maryland, #2 Massachusetts, #3 New Jersey',
    keywords: [
      'median income by state',
      'median household income by state',
      'states by median income',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/16+median+average+income+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/16+median+average+income+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/16+median+average+income+by+state.jpg',
    },
  },

  'median-income-by-year': {
    path: 'median-income-by-year',
    featureConst: MEDIAN_INCOME,
    name: 'Median income by year — Map AI',
    description:
      'Track median income by year across the U.S. with Map AI. Explore trends over time with interactive maps—by state, neighborhood, and down to 0.5-mile areas.',
    keywords: ['real median income by year data', 'median income by year'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/18+real+median+income+by+year+data.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/18+real+median+income+by+year+data.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/18+real+median+income+by+year+data.jpg',
    },
  },

  'neighborhood-crime-report': {
    path: 'neighborhood-crime-report',
    featureConst: CRIME_RATE,
    name: 'Neighborhood crime report | Map AI',
    description:
      'Get a free neighborhood crime report plus local data on demographics, income, and real estate. Know more before you move or invest.',
    keywords: ['neighborhood crime report'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/19+Neighborhood+crime+report.jpg',
      classic1200x900:
        'hhttps://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/19+Neighborhood+crime+report.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/19+Neighborhood+crime+report.jpg',
    },
  },

  'top-ranked-states-education': {
    path: 'top-ranked-states-education',
    featureConst: WISE_INDEX_BY_COUNTRY,
    name: 'Education rankings by state & neighborhood',
    description:
      'Explore rankings for education by state and down to neighborhood. #1 Massachusetts, #2 Maryland, #3 Connecticut',
    keywords: [
      'states ranked by education',
      'education rankings by state',
      'education ranking by state',
      'public education ranking by state',
      'us states ranked by education',
      'united states education ranking by state',
      'education ranked by state',
      'best education ranking by state',
    ],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/20+top+ranked+states+for+public+education.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/20+top+ranked+states+for+public+education.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/20+top+ranked+states+for+public+education.jpg',
    },
  },

  'smoking-rates-by-state': {
    path: 'smoking-rates-by-state',
    featureConst: SMOKING_RATE,
    name: 'Map AI: Smoking rates by states',
    description:
      'Explore smoking rates by U.S. state—plus health, income & demographic data. Explore patterns with interactive maps down to 0.5-mile areas.',
    keywords: ['smoking rates by state'],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/21+smoking+rates+by+state.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/21+smoking+rates+by+state.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/21+smoking+rates+by+state.jpg',
    },
  },

  'population-density-by-city': {
    path: 'population-density-by-city',
    featureConst: PEOPLE_PER_MI2,
    name: 'Map AI: population density by city, United States - down to 0.5 mi neighborhood',
    description:
      'Explore population density by city, growth trends, median age, and age-gender breakdown—viewed on our interactive map with detailed local data.',
    keywords: ['population density by city united states'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/22+population+density+by+city+united+states.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/22+population+density+by+city+united+states.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/22+population+density+by+city+united+states.jpg',
    },
  },

  'cities-by-population-density': {
    path: 'cities-by-population-density',
    featureConst: PEOPLE_PER_MI2,
    name: 'Map AI: cities by population density',
    description:
      'Explore population density by city, growth trends, median age, and age-gender breakdown—viewed on our interactive map with detailed local data.',
    keywords: [
      'population density by city united states',
      'cities by population density',
    ],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/22+population+density+by+city+united+states.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/22+population+density+by+city+united+states.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/22+population+density+by+city+united+states.jpg',
    },
  },

  'map-of-median-household-income': {
    path: 'map-of-median-household-income',
    featureConst: MEDIAN_INCOME,
    name: 'Map of median household income — Map AI',
    description:
      'View the map of median household income across the U.S.—from state to 0.5-mile neighborhoods. Includes trends, comparisons & more.',
    keywords: ['map of median household income'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/24+map+of+median+household+income.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/24+map+of+median+household+income.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/24+map+of+median+household+income.jpg',
    },
  },

  'map-of-median-income': {
    path: 'map-of-median-income',
    featureConst: MEDIAN_INCOME,
    name: 'Map of median income — from counties to neighborhoods',
    description:
      'Explore the map of median income by state and neighborhood. Includes household, family & individual income data with 0.5-mile granularity.',
    keywords: ['map of median income'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/24+map+of+median+household+income.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/24+map+of+median+household+income.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/24+map+of+median+household+income.jpg',
    },
  },

  'homeownership-rate-by-age': {
    path: 'homeownership-rate-by-age',
    featureConst: HOME_OWNERSHIP_RATIO,
    name: 'Homeownership rate by age',
    description:
      'Discover U.S. homeownership rates by age. Use our interactive map to analyze patterns by income, demographics & more—down to 0.5-mile areas.',
    keywords: ['homeownership rate by age'],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/26+homeownership+rate+by+age.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/26+homeownership+rate+by+age.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/26+homeownership+rate+by+age.jpg',
    },
  },

  'median-household-income-by-zip': {
    path: 'median-household-income-by-zip',
    featureConst: MEDIAN_INCOME,
    name: 'Median household income by ZIP code — Map AI',
    description:
      'Explore median household income by ZIP code with data down to 0.5-mile detail. Compare income by region, city, and neighborhood in one map.',
    keywords: ['median household income by zip code map'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/27+median+household+income+by+zip+code+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/27+median+household+income+by+zip+code+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/27+median+household+income+by+zip+code+map.jpg',
    },
  },

  'demographic-map-by-zip-code': {
    path: 'demographic-map-by-zip-code',
    featureConst: POPULATION_GROWTH_TREND,
    name: 'Demographic map by ZIP code',
    description:
      'View the demographic map by ZIP code—age, race, income & more. Interactive and detailed down to 0.5-mile areas across the U.S.',
    keywords: ['demographic map by zip code'],
    level: H3_RES5_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/28+demographic+map+by+zip+code.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/28+demographic+map+by+zip+code.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/28+demographic+map+by+zip+code.jpg',
    },
  },

  'florida-crime-rate-by-county-map': {
    path: 'florida-crime-rate-by-county-map',
    featureConst: CRIME_RATE,
    name: 'Florida crime rate by county map — Map AI',
    description:
      'Get clear Florida crime rate by county map. Total crime, community, business, violent, property.',
    keywords: [
      'florida crime rate by county map',
      'population density by us county',
      'us population density by county',
      'population density by county usa',
      'u.s. population density by county',
      'u.s. counties by population density',
      'us counties by population density',
      'us population density map by county',
      'counties by population density',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/2+Crime+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/2+Crime+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/2+Crime+map.jpg',
    },
  },

  'us-population-density-by-county': {
    path: 'us-population-density-by-county',
    featureConst: PEOPLE_PER_MI2,
    name: 'US population density map by county | Map AI',
    description:
      'Explore an interactive U.S. map showing population density by county. Zoom in to access 100+ data insights for business and lifestyle decisions, with 1-mile precision',
    keywords: [
      'population density by us county',
      'us population density by county',
      'population density by county usa',
      'u.s. population density by county',
      'u.s. counties by population density',
      'us counties by population density',
      'us population density ai map by county',
      'u.s. population density map by county',
      'population density by county',
      'counties by population density',
    ],
    level: COUNTY_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/30+u.s.+population+density+map+by+county.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/30+u.s.+population+density+map+by+county.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/30+u.s.+population+density+map+by+county.jpg',
    },
  },

  'heart-disease-by-state-map': {
    path: 'heart-disease-by-state-map',
    featureConst: HEART_DISEASES_PREVALENCE,
    name: 'Heart disease by state - Map AI',
    description:
      'Explore heart disease rates by state with Map AI. View trends, risk factors & health disparities—mapped down to 0.5-mile areas for local insights.',
    keywords: ['heart disease by state map'],
    level: STATE_LEVEL_LAYER,
    images: {
      wide1200x675:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85675/31+heart+disease+by+state+map.jpg',
      classic1200x900:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%85900/31+heart+disease+by+state+map.jpg',
      square1200:
        'https://map-ai-static-public.s3.us-east-1.amazonaws.com/1200%D1%851200/31+heart+disease+by+state+map.jpg',
    },
  },
};

export const CITIES: Record<string, CityCfg> = {
  'new-york': {
    path: 'new-york',
    name: 'New York',
    center: [-74.006, 40.7128],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['NY', 'NYC'],
    description:
      'Global capital of finance and culture with unmatched transit and dense, walkable neighborhoods. Diverse job market and constant foot traffic make it ideal for data-driven location decisions.',
  },
  'los-angeles': {
    path: 'los-angeles',
    name: 'Los Angeles',
    center: [-118.2437, 34.0522],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['LA'],
    description:
      'Sprawling coastal metro anchored by entertainment, media, and tech. Sunny climate and car-first mobility shape retail clusters and neighborhood demand patterns.',
  },
  boston: {
    path: 'boston',
    name: 'Boston',
    center: [-71.0589, 42.3601],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    description:
      'Historic, compact city with top universities and a strong biotech corridor. High foot traffic, transit access, and affluent suburbs drive premium consumer demand.',
  },
  chicago: {
    path: 'chicago',
    name: 'Chicago',
    center: [-87.6298, 41.8781],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CHI'],
    description:
      'Lakefront hub with iconic architecture and a diversified economy. Dense core and distinct neighborhoods offer varied demographics and spending profiles.',
  },
  houston: {
    path: 'houston',
    name: 'Houston',
    center: [-95.3698, 29.7604],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['HOU'],
    description:
      'Energy and healthcare powerhouse with rapid suburban growth. Large trade area, diverse communities, and business-friendly climate shape consumer patterns.',
  },
  phoenix: {
    path: 'phoenix',
    name: 'Phoenix',
    center: [-112.074, 33.4484],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['PHX'],
    description:
      'Fast-growing desert metro with abundant sunshine and new housing. Expanding suburbs and inbound migration drive evolving retail and service demand.',
  },
  philadelphia: {
    path: 'philadelphia',
    name: 'Philadelphia',
    center: [-75.1652, 39.9526],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['PHL', 'Philly'],
    description:
      'Historic East Coast city with strong eds-and-meds anchors. Rowhouse neighborhoods and transit corridors create stable local catchments for businesses.',
  },
  'san-antonio': {
    path: 'san-antonio',
    name: 'San Antonio',
    center: [-98.4936, 29.4241],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SAT'],
    description:
      'Family-friendly market known for the River Walk and military presence. Growing tech and manufacturing add to steady household formation.',
  },
  'san-diego': {
    path: 'san-diego',
    name: 'San Diego',
    center: [-117.1611, 32.7157],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SD'],
    description:
      'Coastal city with mild climate, strong biotech, and a major naval base. Tourism and high quality of life support premium retail and services.',
  },
  dallas: {
    path: 'dallas',
    name: 'Dallas',
    center: [-96.797, 32.7767],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DAL', 'DFW'],
    description:
      'Corporate HQ magnet with pro-business policies and rapid in-migration. Expansive suburbs create multiple high-growth trade areas.',
  },
  'san-jose': {
    path: 'san-jose',
    name: 'San Jose',
    center: [-121.8863, 37.3382],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SJ'],
    description:
      'Heart of Silicon Valley and high-income tech workforce. Suburban campuses and commuting patterns drive daytime population shifts.',
  },
  austin: {
    path: 'austin',
    name: 'Austin',
    center: [-97.7431, 30.2672],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['ATX'],
    description:
      'Booming tech and creative hub with a vibrant music scene. Strong startup activity and rapid population growth reshape local demand.',
  },
  jacksonville: {
    path: 'jacksonville',
    name: 'Jacksonville',
    center: [-81.6557, 30.3322],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['JAX'],
    description:
      'Florida’s largest city by land area with port, logistics, and naval anchors. Coastal lifestyle and suburban expansion drive housing and retail growth.',
  },
  'fort-worth': {
    path: 'fort-worth',
    name: 'Fort Worth',
    center: [-97.3308, 32.7555],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['FTW'],
    description:
      'Western heritage meets modern aerospace and defense. Paired with Dallas in the DFW region, it offers diverse neighborhoods and steady in-migration.',
  },
  columbus: {
    path: 'columbus',
    name: 'Columbus',
    center: [-82.9988, 39.9612],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CMH'],
    description:
      'State capital with a large university and growing innovation scene. Stable employment base supports consistent consumer demand.',
  },
  charlotte: {
    path: 'charlotte',
    name: 'Charlotte',
    center: [-80.8431, 35.2271],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['CLT'],
    description:
      'Sun Belt banking center with fast-rising population. New master-planned communities and light-rail growth corridors create fresh retail nodes.',
  },
  'san-francisco': {
    path: 'san-francisco',
    name: 'San Francisco',
    center: [-122.4194, 37.7749],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SF'],
    description:
      'Iconic, walkable core on a dramatic bay-and-hills landscape. Tech, tourism, and premium dining fuel dense, high-spend micromarkets.',
  },
  indianapolis: {
    path: 'indianapolis',
    name: 'Indianapolis',
    center: [-86.1581, 39.7684],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['Indy'],
    description:
      'Logistics and sports hub with an affordable cost of living. Steady suburban growth and major events shape seasonal foot traffic.',
  },
  seattle: {
    path: 'seattle',
    name: 'Seattle',
    center: [-122.3321, 47.6062],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['SEA'],
    description:
      'Waterfront tech hub with global brands and a major port. High incomes and outdoor lifestyle drive premium consumer segments.',
  },
  denver: {
    path: 'denver',
    name: 'Denver',
    center: [-104.9903, 39.7392],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DEN'],
    description:
      'Mile-High City at the base of the Rockies with strong aerospace and tech. Active lifestyle and rapid migration power new neighborhood hotspots.',
  },
  'washington-dc': {
    path: 'washington-dc',
    name: 'Washington, D.C.',
    center: [-77.0369, 38.9072],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DC'],
    description:
      'Nation’s capital with federal employment, think tanks, and tourism. Transit-rich core and stable incomes support consistent local demand.',
  },
  detroit: {
    path: 'detroit',
    name: 'Detroit',
    center: [-83.0458, 42.3314],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['DET'],
    description:
      'Automotive heritage city with ongoing downtown revitalization. Manufacturing base and affordable housing create value-focused opportunities.',
  },
  nashville: {
    path: 'nashville',
    name: 'Nashville',
    center: [-86.7816, 36.1627],
    zoom: 10,
    level: H3_RES5_LEVEL_LAYER,
    aliases: ['BNA'],
    description:
      'Music City with a strong healthcare sector and booming tourism. Rapid population growth fuels new retail, dining, and entertainment clusters.',
  },
};

export const OVERRIDES: OverrideCfg[] = [];

export const buildUrlCityFeature = (cityPath: string, featurePath: string) =>
  `/${cityPath}/${featurePath}`;

export const buildUrlFeature = (featurePath: string) => `/${featurePath}`;

export function pickPageMeta(params: {
  cityPath?: string | null;
  featurePath: string;
}): PageMeta | null {
  const feat = FEATURES[params.featurePath];
  if (!feat) return null;

  const applyOverrides = (o?: OverrideCfg) => {
    const title = o?.title ?? feat.name;
    const description = o?.description ?? feat.description;

    const keywords = (o?.keywords ??
      (feat as any).keywords) as PageMeta['keywords'];

    const images = {
      ...(feat as any).images,
      ...(o?.images || {}),
    } as PageMeta['images'];

    const level = (o?.level ?? (feat as any).level) as PageMeta['level'];

    return { title, description, keywords, images, level };
  };

  if (params.cityPath) {
    const city = CITIES[params.cityPath];
    if (!city) return null;

    const o = OVERRIDES.find(
      (x) => x.feature === feat.path && x.city === city.path
    );
    const merged = applyOverrides(o);

    return {
      urlFull: `/${city.path}/${feat.path}`,
      title: merged.title ?? `${city.name}: ${feat.name}`,
      description: merged.description,
      city,
      feature: feat,
      keywords: merged.keywords,
      images: merged.images,
      level: merged.level,
    };
  } else {
    const o = OVERRIDES.find((x) => x.feature === feat.path && !x.city);
    const merged = applyOverrides(o);

    return {
      urlFull: `/${feat.path}`,
      title: merged.title ?? `${feat.name}`,
      description: merged.description,
      feature: feat,
      keywords: merged.keywords,
      images: merged.images,
      level: merged.level,
    };
  }
}
